import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getDealerPortalSectionValue } from 'enums/DealerPortalSectionEnum';
import { LanguageEnumValue, LanguageValue } from 'enums/LanguageEnum';
import { IsImpersonationMode } from 'config/config';
import { useDealerPortalSectionLandingPage } from 'services/Data/DealerPortalMenuServices';
import { useDealer } from 'services/Data/DealerServices';
import { translateLabel } from 'services/Utilities/labelUtils';
import useImage from 'services/Hooks/useImage';

const normalizeColor = value => {
  const normalizedValue = (value || '').trim();
  return /^#([0-9A-F]{3}){1,2}$/i.test(normalizedValue) ? normalizedValue : '#000000';
};

const normalizeTextColor = value => {
  const normalizedValue = (value || '').trim();
  return /^#([0-9A-F]{3}){1,2}$/i.test(normalizedValue) ? normalizedValue : '#FFFFFF';
};

const SLIDE_MS = 6000;
const XFADE_MS = 800;
const AUTOPLAY_RESUME_MS = 15000;

const normalizeTargetPath = (target, sectionName) => {
  const fallbackPath = `/dealer/section/${sectionName}`;

  if (!target || typeof target !== 'string') {
    return fallbackPath;
  }

  if (!target.startsWith('/dealer/')) {
    return fallbackPath;
  }

  return target;
};

const normalizeLanguageId = languageId => {
  const normalizedValue = `${languageId || ''}`.trim();
  return normalizedValue || null;
};

const resolveDealerLanguageId = dealerLanguageEnum => {
  const normalizedDealerLanguage = Number(dealerLanguageEnum);

  if (normalizedDealerLanguage === LanguageEnumValue.FRENCH) {
    return LanguageValue.FRENCH;
  }

  return LanguageValue.ENGLISH;
};

const DealerPortalSectionLandingPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { sectionName } = useParams();
  const accountReducer = useSelector(state => state.AccountReducer);
  const { data: dealer } = useDealer({ enabled: accountReducer?.isLoggedIn === true });
  const { getDealerPortalMenuImage } = useImage();

  const sectionValue = getDealerPortalSectionValue(sectionName);
  const { data: sectionLandingData } = useDealerPortalSectionLandingPage(sectionValue, sectionValue > 0);
  const dealerLanguageEnum = useMemo(() => {
    const profileLanguage = Number(accountReducer?.profile?.person?.languageEnum);
    const currentDealerLanguage = Number(dealer?.language);
    const preferredLanguage = IsImpersonationMode ? profileLanguage : currentDealerLanguage;

    if (preferredLanguage === LanguageEnumValue.FRENCH || preferredLanguage === LanguageEnumValue.ENGLISH) {
      return preferredLanguage;
    }

    if (profileLanguage === LanguageEnumValue.FRENCH || profileLanguage === LanguageEnumValue.ENGLISH) {
      return profileLanguage;
    }

    return LanguageEnumValue.ENGLISH;
  }, [dealer?.language, accountReducer?.profile?.person?.languageEnum]);
  const isFrench = dealerLanguageEnum === LanguageEnumValue.FRENCH;
  const dealerLanguageCode = isFrench ? 'fr' : 'en';
  const targetPath = useMemo(() => {
    const queryString = new URLSearchParams(location.search);
    return normalizeTargetPath(queryString.get('target'), sectionName);
  }, [location.search, sectionName]);

  const title = translateLabel(sectionLandingData?.sectionLandingTitle, dealerLanguageCode) || t('sectionLandingPage');
  const subtitle = translateLabel(sectionLandingData?.sectionLandingSubtitle, dealerLanguageCode) || '';
  const buttonText = translateLabel(sectionLandingData?.sectionLandingButtonText, dealerLanguageCode) || t('continue');
  const showButton = sectionLandingData?.sectionLandingShowButton !== false;
  const useBackgroundColor = Boolean(sectionLandingData?.sectionLandingUseBackgroundColor);
  const backgroundColor = normalizeColor(sectionLandingData?.sectionLandingBackgroundColor);
  const titleColor = normalizeTextColor(sectionLandingData?.sectionLandingTitleColor);
  const subtitleColor = normalizeTextColor(sectionLandingData?.sectionLandingSubtitleColor);
  const requestedLanguageId = useMemo(() => resolveDealerLanguageId(dealerLanguageEnum), [dealerLanguageEnum]);

  const landingSlides = useMemo(() => {
    if (!sectionLandingData?.menuId) {
      return [];
    }

    const orderedImages = (sectionLandingData?.sectionLandingImages || [])
      .slice()
      .sort((left, right) => left.displayOrder - right.displayOrder)
      .filter(image => image?.dealerPortalMenuAttachedFile_Id)
      .map(image => ({
        fileId: image.dealerPortalMenuAttachedFile_Id,
        languageId:
          normalizeLanguageId(image.language_Id) ||
          normalizeLanguageId(image.dealerPortalMenuAttachedFile?.language_Id) ||
          null,
      }));

    const filteredImages = orderedImages.filter(image => image.languageId === requestedLanguageId || !image.languageId);

    const deduplicatedFileIds = Array.from(new Set(filteredImages.map(image => image.fileId)));
    return deduplicatedFileIds.map(fileId => ({
      id: fileId,
      imgSrc: getDealerPortalMenuImage(sectionLandingData.menuId, fileId),
    }));
  }, [sectionLandingData, getDealerPortalMenuImage, requestedLanguageId]);

  const hasMultipleSlides = landingSlides.length > 1;
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoplayTimerRef = useRef(null);
  const resumeTimerRef = useRef(null);

  useEffect(() => {
    if (currentSlideIndex >= landingSlides.length) {
      setCurrentSlideIndex(0);
    }
  }, [currentSlideIndex, landingSlides.length]);

  useEffect(() => {
    if (!hasMultipleSlides || !isAutoPlaying) {
      clearInterval(autoplayTimerRef.current);
      return () => {};
    }

    const start = () => {
      clearInterval(autoplayTimerRef.current);
      autoplayTimerRef.current = setInterval(() => {
        setCurrentSlideIndex(previousIndex => (previousIndex + 1) % landingSlides.length);
      }, SLIDE_MS);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(autoplayTimerRef.current);
        return;
      }

      start();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    start();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(autoplayTimerRef.current);
    };
  }, [hasMultipleSlides, isAutoPlaying, landingSlides.length]);

  useEffect(
    () => () => {
      clearInterval(autoplayTimerRef.current);
      clearTimeout(resumeTimerRef.current);
    },
    [],
  );

  const pauseAndScheduleResume = () => {
    setIsAutoPlaying(false);
    clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      setIsAutoPlaying(true);
    }, AUTOPLAY_RESUME_MS);
  };

  const jumpToSlide = delta => {
    if (!hasMultipleSlides) {
      return;
    }

    pauseAndScheduleResume();
    setCurrentSlideIndex(previousIndex => (previousIndex + delta + landingSlides.length) % landingSlides.length);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: useBackgroundColor ? backgroundColor : '#2A0507',
        cursor: showButton ? 'default' : 'pointer',
      }}>
      {!showButton ? (
        <Box
          onClick={() => history.push(targetPath)}
          role="button"
          tabIndex={0}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              history.push(targetPath);
            }
          }}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 4,
            background: 'transparent',
            cursor: 'pointer',
          }}
        />
      ) : null}

      {!useBackgroundColor
        ? landingSlides.map((slide, index) => (
            <Box
              key={slide.id}
              component="img"
              src={slide.imgSrc}
              alt={title}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                opacity: index === currentSlideIndex ? 1 : 0,
                transition: `opacity ${XFADE_MS}ms ease-in-out`,
                willChange: 'opacity',
              }}
              loading={index === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
          ))
        : null}

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.7) 100%)',
          zIndex: 1,
        }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: { xs: 3, md: 6 },
            pointerEvents: 'none',
          }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: 44, sm: 64, md: 88 },
              lineHeight: 1.06,
              fontWeight: 900,
              maxWidth: 1080,
              color: titleColor,
              textTransform: 'uppercase',
              textShadow: '0 2px 12px rgba(0,0,0,0.65)',
            }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              variant="body1"
              sx={{
                mt: 2,
                maxWidth: 920,
                mx: 'auto',
                fontSize: { xs: 18, md: 28 },
                lineHeight: 1.4,
                fontWeight: 500,
                color: subtitleColor,
                textShadow: '0 2px 12px rgba(0,0,0,0.65)',
              }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>

        {showButton ? (
          <Button
            variant="contained"
            color="secondary"
            size="large"
            disableElevation
            onClick={() => history.push(targetPath)}
            sx={{
              py: { xs: 1.5, md: 2.5 },
              px: { xs: 2.5, md: 4 },
              borderRadius: 0,
              boxShadow: 'none',
              position: 'absolute',
              right: { xs: 16, md: 24 },
              bottom: { xs: 16, md: 24 },
              zIndex: 7,
              '&:hover': {
                backgroundColor: '#EFBA29',
                boxShadow: 'none',
              },
              '&:active': {
                boxShadow: 'none',
              },
              '&.Mui-focusVisible': {
                boxShadow: 'none',
              },
            }}>
            <Typography
              variant="h1"
              sx={{
                color: '#000',
                fontSize: { xs: 18, md: 26 },
                letterSpacing: '-0.02em',
                lineHeight: 1,
                textTransform: 'uppercase',
              }}>
              {buttonText}
            </Typography>
          </Button>
        ) : null}

        {hasMultipleSlides ? (
          <>
            <IconButton
              onClick={() => jumpToSlide(-1)}
              sx={theme => ({
                position: 'absolute',
                left: { xs: 8, md: 16 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 6,
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': { bgcolor: theme.palette.primary.light },
              })}>
              <ChevronLeft />
            </IconButton>

            <IconButton
              onClick={() => jumpToSlide(1)}
              sx={theme => ({
                position: 'absolute',
                right: { xs: 8, md: 16 },
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 6,
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': { bgcolor: theme.palette.primary.light },
              })}>
              <ChevronRight />
            </IconButton>

            <Stack
              direction="row"
              spacing={1}
              sx={{
                position: 'absolute',
                bottom: 18,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 6,
              }}>
              {landingSlides.map((slide, index) => (
                <Box
                  key={slide.id || index}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    pauseAndScheduleResume();
                    setCurrentSlideIndex(index);
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      pauseAndScheduleResume();
                      setCurrentSlideIndex(index);
                    }
                  }}
                  sx={theme => ({
                    width: currentSlideIndex === index ? 28 : 12,
                    height: 12,
                    borderRadius: 6,
                    bgcolor: currentSlideIndex === index ? theme.palette.primary.main : 'rgba(255,255,255,0.55)',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      bgcolor: currentSlideIndex === index ? theme.palette.primary.main : 'rgba(255,255,255,0.8)',
                    },
                  })}
                />
              ))}
            </Stack>
          </>
        ) : null}
      </Box>
    </Box>
  );
};

export default DealerPortalSectionLandingPage;
